import { type ReactNode, useMemo } from "react"
import { Icon } from "@/components/Icon"
import { MessageList } from "@/features/chat/components/MessageList"
import { Composer } from "@/features/chat/components/Composer"
import type { ChatMessage } from "@/features/chat/components/MessageBubble"
import type { CheckpointMessage } from "@/features/oio/hooks/useOIORewriteAgent.types"

interface RewriteChatPanelProps {
  checkpointMessages: CheckpointMessage[]
  isLoading: boolean
  hasDraft: boolean
  draftStatus: string
  onSend: (content: string) => void
  /** Mid-review evidence attach — surfaces the paperclip when provided. */
  onAttachEvidence?: (file: File) => Promise<void> | void
  attachBusy?: boolean
  hitlNode?: ReactNode
  /**
   * The locally-rendered completion summary card (OrderSummaryCard). Rendered
   * at the head of the message stream — it chronologically precedes any
   * rewrite conversation and never enters the runtime's message history.
   */
  summaryCard?: ReactNode
  /**
   * The empty-stream copy. Defaults to the Order-in-Original text; another
   * order type's chat (the Order-in-Appeal chat) passes its own.
   */
  emptyState?: { title: string; body: string }
  /** Composer placeholder; defaults to the OIO text. */
  placeholder?: string
  /**
   * Disable the composer for a reason the panel cannot see (the OIA chat
   * while a revise is running). Added to `isLoading || !hasDraft`, never
   * instead of it.
   */
  composerDisabled?: boolean
}

const DEFAULT_EMPTY_STATE = {
  title: "Draft ready for review",
  body:
    "The Order-in-Original is on the right for your section-by-section review. Ask anything about this case — the facts, the provisions cited, the demand — or request a change to any section.",
}
const DEFAULT_PLACEHOLDER = "Ask anything about this case…"

function isInternalArtifact(content: string): boolean {
  const t = content.trimStart()
  if (!t.startsWith("{") && !t.startsWith("[")) return false
  return /"(?:__doc_urls|artifact_id|stage_name|scn_fact_findings|document_support_matrix|department_primary_burden_items|corroborative_sources_checked)"/.test(
    t,
  )
}

function checkpointMessageToChatMessage(m: CheckpointMessage, index: number): ChatMessage | null {
  if (m.type !== "human" && m.type !== "ai") return null
  const raw = m.content
  const content = (typeof raw === "string" ? raw : (raw as { text?: string } | null | undefined)?.text ?? "").trim()
  if (!content) return null
  if (isInternalArtifact(content)) return null
  return {
    id: m.id ?? `checkpoint-${index}`,
    role: m.type === "human" ? "user" : "assistant",
    content,
    createdAt: undefined,
  }
}

export function RewriteChatPanel({
  checkpointMessages,
  isLoading,
  hasDraft,
  draftStatus,
  onSend,
  onAttachEvidence,
  attachBusy,
  hitlNode,
  summaryCard,
  emptyState = DEFAULT_EMPTY_STATE,
  placeholder = DEFAULT_PLACEHOLDER,
  composerDisabled: forceDisabled = false,
}: RewriteChatPanelProps) {
  const messages = useMemo<ChatMessage[]>(
    () =>
      checkpointMessages
        .map((m, i) => checkpointMessageToChatMessage(m, i))
        .filter((m): m is ChatMessage => m !== null),
    [checkpointMessages],
  )

  const draftMissing = draftStatus === "draft_completed" && !hasDraft
  const composerDisabled = isLoading || !hasDraft || forceDisabled

  return (
    <div className="oio-chatpanel">
      {draftStatus === "draft_failed_terminal" ? (
        <div className="chat__stream">
          <div className="empty" style={{ height: "auto", padding: "40px 20px" }}>
            <div className="empty__ico">
              <Icon name="alert" size={22} />
            </div>
            <h3>Generation was interrupted</h3>
            <p>Use the order panel on the right to retry or report the issue.</p>
          </div>
        </div>
      ) : draftMissing ? (
        <div className="chat__stream">
          <div className="empty" style={{ height: "auto", padding: "40px 20px" }}>
            <div className="empty__ico">
              <Icon name="alert" size={22} />
            </div>
            <h3>Draft not available</h3>
            <p>
              This case is complete, but its draft isn't available here. Regenerate it from the
              order panel on the right.
            </p>
          </div>
        </div>
      ) : (
        <MessageList
          messages={messages}
          isLoading={isLoading}
          lead={summaryCard}
          empty={emptyState}
        />
      )}
      {hitlNode}
      <Composer
        onSend={onSend}
        disabled={composerDisabled}
        placeholder={placeholder}
        onAttach={onAttachEvidence}
        attachBusy={attachBusy}
      />
    </div>
  )
}
